/**
 * §5 確定履歴から取込 → 参照列 フロー hook (AC-DFD-029/030/031/032,
 * s07-confirm-history-import-reference-view.md)。
 *
 * ツールバー「確定履歴から取込」ボタン → 確定履歴選択ダイアログ
 * (ConfirmRecordImportDialog) → 1件選択して「取込」→ 即座に
 * PreviewConfirmRecord mutation を実行する (追加の確認ダイアログを挟まない、
 * AC-DFD-029/031 の逐語)。
 *
 * 旧・書込み型 RPC との違い: 応答は編集中の draft (editRows) を
 * 一切変更しない。代わりに参照列 state (confirmRecordRefs) に追加され、
 * KomaGrid の追加列 / ForecastChart の系列として非破壊的に表示される
 * (過去実績 pastActualDateStrs と並列の独立 state、Decision #2)。参照列は
 * 独立 5 件上限 (Decision #3、confirmRecordRef.ts の MAX_CONFIRM_RECORD_REFS)。
 *
 * scope 分岐 (AC-DFD-032): targetScope='RETAILER' のときは選択中の 1 小売へ
 * scope=RETAIL で preview する。targetScope='BG_TOTAL' のときは
 * scope=BG_TOTAL + bg_retailer_business_cds (現在の case 配下 retailer 一覧) を
 * 送り、応答の per-retailer bgResults を保持する (ADR
 * demand-forecast-bg-import-per-retailer-direct: 按分せず per-retailer 直接
 * 表示)。retailer scope への切替時の値解決は confirmRecordRef.ts
 * (resolveConfirmRecordRefColumns) が担う。
 *
 * 楽観ロックは無い (preview は draft を一切読み書きしないため
 * expected_updated_at を持たない、AC-10 と同型)。Aborted は発生しない契約だが、
 * usePreviewSimpleForecast と同様に防御的に一般エラー扱いへフォールバックする。
 */
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { create } from '@bufbuild/protobuf'
import { ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  AggregationScope,
  ConfirmCategory,
  type DemandConfirmRecordSummary,
  PlanningSection,
} from '@ebalance/proto/bffuser/v1/demand_confirm_record_model_pb'
import type {
  RetailerDraft,
  RetailerPreviewEntry,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { PreviewConfirmRecordRequestSchema } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import { appToaster } from '~/libs/toaster'
import { isSameProtoDate } from '~/models/dateTime'
import { useListDemandConfirmRecords } from '~/pages/DemandForecastDaily/hooks/useListDemandConfirmRecords'
import { usePreviewConfirmRecord } from '~/pages/DemandForecastDaily/hooks/usePreviewConfirmRecord'
import {
  CONFIRM_REF_LEGEND_KEY_PREFIX,
  type ConfirmRecordRef,
  MAX_CONFIRM_RECORD_REFS,
  type ResolvedConfirmRecordRefColumn,
  confirmRefLegendKey,
  resolveConfirmRecordRefColumns,
} from '~/pages/DemandForecastDaily/models/confirmRecordRef'
import { runImportMutation } from '~/pages/DemandForecastDaily/models/runImportMutation'
import { resolveConfirmRecordPreviewDetailedErrorMessage } from '~/pages/DemandForecastDaily/models/serverError'
import type { TargetScope } from '~/pages/DemandForecastDaily/models/types'

type Params = {
  caseId: string | undefined
  selectedAreaCode: AreaCode | undefined
  section: WorkflowSection
  targetDate: ProtoDate | undefined
  /**
   * 現在の case の BG (useGetWorkflowCase(caseId).bgCode)。確定履歴一覧をこの BG に
   * 絞り込む。未解決 (undefined) の間は一覧取得を抑止する (他 BG の履歴が候補に
   * 混じる race を防ぐ)。
   */
  bgCode: string | undefined
  /** BG全体 / 小売ビュー切替 (AC-DFD-032: BG_TOTAL でも取込可) */
  targetScope: TargetScope
  /** 現在選択中の小売 (小売ビューのみ使用) */
  selectedRetailerBusinessCd: string | undefined
  /** BG_TOTAL scope の bg_retailer_business_cds 解決元 (現在 case 配下の retailer 一覧) */
  retailers: RetailerDraft[]
  /**
   * 凡例 hiddenKeys の set-fn (App root の DemandForecastLegendProvider 由来)。
   * 参照列の削除/クリア/リセット時に、対応する confirmRef_<id> hiddenKey を
   * flow 内で連動除去するために注入する (index 側に同期コードを漏らさない)。
   * codex review #5041: 凡例で非表示にした参照列を削除しても hiddenKey が残ると、
   * 同じ確定記録を再追加した時に id 一致で hide=true が復活し、グリッドには列が
   * 出るがチャートだけ非表示になる不変条件違反を招く。この連動でそれを防ぐ。
   */
  setLegendHiddenKeys: Dispatch<SetStateAction<Set<string>>>
}

export type UseConfirmRecordImportFlowResult = {
  /** §5 確定履歴選択ダイアログの開閉状態 */
  isConfirmRecordDialogOpen: boolean
  /** ツールバー「確定履歴から取込」ボタン → 選択ダイアログを開く */
  openConfirmRecordDialog: () => void
  /** 確定履歴選択ダイアログを閉じる */
  closeConfirmRecordDialog: () => void
  /**
   * 確定履歴選択ダイアログで 1 件選択して「取込」→ 追加の確認ダイアログを挟まず
   * 即座に参照列プレビューを実行する (AC-DFD-029/031)。
   */
  selectConfirmRecord: (recordId: string) => Promise<void>
  /**
   * 取込候補の確定履歴 (現在の対象日・scope に絞り込み済み)。取得に失敗している間は
   * 取得済みの古い一覧があっても空を返す (候補として選ばせない)。
   */
  confirmRecords: DemandConfirmRecordSummary[]
  /** 一覧の取得中 (失敗後の再試行中を含む)。`isRecordsError` とは排他 */
  isRecordsLoading: boolean
  /** 一覧の取得に失敗した (再試行中は false) */
  isRecordsError: boolean
  /** 一覧の取得をやり直す */
  retryRecords: () => void
  /** mutation 実行中かどうか (ConfirmRecordImportDialog の isLoading に渡す) */
  isImporting: boolean
  /**
   * 現在の表示 (targetScope / selectedRetailerBusinessCd) に解決済みの参照列
   * (KomaGrid の pastActualSlots と concat して渡す、Decision #2)。
   */
  confirmRecordRefColumns: ResolvedConfirmRecordRefColumn[]
  /** 参照列 chip / ヘッダの × クリックで 1 件解除する */
  handleConfirmRecordRefRemove: (id: string) => void
  /** 参照列を全解除する (過去実績「クリア」と共通導線、Decision #5) */
  handleConfirmRecordRefsClear: () => void
  /** すでに参照列として追加した確定履歴の id (ダイアログが重複の選択を止める、AC-DFD-064) */
  addedRecordIds: ReadonlySet<string>
  /** 参照列 5 件上限に達しているか (ダイアログ「取込」ボタン disable + tooltip) */
  isConfirmRecordRefsAtLimit: boolean
  /**
   * 確定レコード id (= KomaGrid の参照列採用キー) から BG_TOTAL scope 追加時の
   * per-retailer bgResults を解決する (design note
   * tmp/designing-feature/demand-fix-aggregate-reference-adopt.md Decision 5)。
   * BG_TOTAL scope で追加した ref 以外 (RETAIL scope 追加・存在しない id) は
   * undefined — per-retailer 採用は BG_TOTAL 追加時の ref にのみ意味を持つため。
   */
  resolveBgResultsForRef: (
    confirmRecordId: string,
  ) => RetailerPreviewEntry[] | undefined
}

/** §5 確定履歴から取込 → 参照列フローの state + handler をまとめて返す。 */
export function useConfirmRecordImportFlow({
  caseId,
  selectedAreaCode,
  section,
  targetDate,
  targetScope,
  selectedRetailerBusinessCd,
  bgCode,
  retailers,
  setLegendHiddenKeys,
}: Params): UseConfirmRecordImportFlowResult {
  const { t } = useTranslation()

  const [isConfirmRecordDialogOpen, setIsConfirmRecordDialogOpen] =
    useState(false)
  const [confirmRecordRefs, setConfirmRecordRefs] = useState<
    ConfirmRecordRef[]
  >([])

  // 凡例 hiddenKeys から confirmRef_ prefix の key を一括除去する。参照列を
  // 捨てる 2 経路 (case/エリア/対象日切替の reset、handleConfirmRecordRefsClear)
  // で共有する。参照列だけ捨てて hiddenKey が残ると、同じ確定記録を別コンテキストで
  // 再追加した時に stable id key が再一致して hide=true が復活し、grid に列は出るが
  // チャートだけ非表示になる (codex review #5041)。
  const dropAllConfirmRefHiddenKeys = useCallback((): void => {
    setLegendHiddenKeys((prev) => {
      const next = new Set(
        [...prev].filter((k) => !k.startsWith(CONFIRM_REF_LEGEND_KEY_PREFIX)),
      )
      return next.size === prev.size ? prev : next
    })
  }, [setLegendHiddenKeys])

  // ケース / エリア / 対象日が切り替わったら参照列 state をリセットする。
  // 同一 route 内での caseId クエリ切替では component が再利用されるため、
  // reset しないと前ケースの confirm_record_id を持った参照列が新ケースの
  // grid / chart に残る (cross-case data leak)。primitive deps を使い
  // proto Date の reference identity 変化で誤 fire しないようにする。
  // 参照列と同じ trigger で対応する confirmRef_ hiddenKey も捨てる (旧 index.tsx の
  // 別 useEffect を hook に閉じ込めた、C2 refactor)。
  useEffect(() => {
    setConfirmRecordRefs([])
    dropAllConfirmRefHiddenKeys()
  }, [
    caseId,
    selectedAreaCode,
    targetDate?.year,
    targetDate?.month,
    targetDate?.day,
    dropAllConfirmRefHiddenKeys,
  ])

  const { mutateAsync: previewConfirmRecordAsync, isPending: isImporting } =
    usePreviewConfirmRecord()

  // 確定履歴一覧。断面は絞り込まず (PlanningSection.UNSPECIFIED = 全断面。確定履歴の
  // 参照列プレビューの検証は断面ではなく target_date 一致のため、同一 target_date 内の
  // 断面をまたいだ取込を候補に含める、danmen drift 監査 hit-01)、エリアのみ RPC 側で
  // 絞る。target_date 一致と scope (小売単位 / BG全体) は
  // ListDemandConfirmRecordsRequest に対応 field が無いため下で client 側で絞る。
  // ダイアログ open だけでは case の読み込みを待たない: bgCode が解決するまで取得を
  // 抑止し、他 BG 履歴が一瞬でも候補に見えないようにする。
  const {
    data: recordListData,
    isLoading: isRecordsInitialLoading,
    isError: isRecordsQueryError,
    isFetching: isRecordsFetching,
    refetch: refetchRecords,
  } = useListDemandConfirmRecords({
    planningSectionFilter: PlanningSection.UNSPECIFIED,
    areaCodeFilter: selectedAreaCode,
    confirmCategoryFilter: ConfirmCategory.UNSPECIFIED,
    bgCode,
    enabled: isConfirmRecordDialogOpen && bgCode !== undefined,
  })
  // 失敗後のやり直し中は query の status が error のまま (isLoading は false) なので、
  // 読込中として扱って失敗表示を下げる。 isRecordsLoading / isRecordsError は排他で、
  // dialog はこの 2 値をそのまま描画する。
  const isRecordsLoading =
    isRecordsInitialLoading || (isRecordsQueryError && isRecordsFetching)
  const isRecordsError = isRecordsQueryError && !isRecordsFetching
  const retryRecords = useCallback((): void => {
    void refetchRecords()
  }, [refetchRecords])

  const confirmRecords = useMemo((): DemandConfirmRecordSummary[] => {
    // 失敗中は、キャッシュ済みの前回結果を一切候補にしない。
    if (isRecordsQueryError || recordListData === undefined) return []
    // 候補は target_date が現在の対象日と一致するものに限定する (= 「同一 target_date
    // 内での cross-section 取込」は許容しつつ、無関係な別日の確定履歴が候補に混ざる
    // のは防ぐ)。
    const sameTargetDate = recordListData.records.filter((r) =>
      isSameProtoDate(r.targetDate, targetDate),
    )
    if (targetScope === 'BG_TOTAL') {
      return sameTargetDate.filter(
        (r) => r.aggregationScope === AggregationScope.BG_TOTAL,
      )
    }
    if (selectedRetailerBusinessCd === undefined) return []
    return sameTargetDate.filter(
      (r) =>
        r.aggregationScope === AggregationScope.RETAIL &&
        r.scopeIdentifier === selectedRetailerBusinessCd,
    )
  }, [
    isRecordsQueryError,
    recordListData,
    selectedRetailerBusinessCd,
    targetScope,
    targetDate,
  ])

  const openConfirmRecordDialog = useCallback((): void => {
    setIsConfirmRecordDialogOpen(true)
  }, [])

  const closeConfirmRecordDialog = useCallback((): void => {
    setIsConfirmRecordDialogOpen(false)
  }, [])

  const addedRecordIds = useMemo(
    () => new Set(confirmRecordRefs.map((ref) => ref.id)),
    [confirmRecordRefs],
  )

  const isConfirmRecordRefsAtLimit =
    confirmRecordRefs.length >= MAX_CONFIRM_RECORD_REFS

  // AC-DFD-029/031: 1件選択して「取込」を押した時点で追加の確認ダイアログを
  // 挟まず即座に参照列プレビューを実行する。
  const selectConfirmRecord = useCallback(
    async (recordId: string): Promise<void> => {
      if (
        selectedAreaCode === undefined ||
        targetDate === undefined ||
        caseId === undefined ||
        isConfirmRecordRefsAtLimit
      )
        return

      const isBgTotal = targetScope === 'BG_TOTAL'
      if (!isBgTotal && selectedRetailerBusinessCd === undefined) return

      // 追加済みの確定履歴は、ダイアログ側で「取込」を非活性にして選ばせない
      // (AC-DFD-064。addedRecordIds を渡す)。

      // reason 別文言解決の受け皿 (matches で解決し handle で読む、runImportMutation
      // の ImportMutationErrorHandler は handle() に err を渡さない契約のため
      // クロージャで橋渡しする)。
      let resolvedReasonMessage: string | undefined
      await runImportMutation({
        mutateAsync: () =>
          previewConfirmRecordAsync(
            create(PreviewConfirmRecordRequestSchema, {
              caseId,
              areaCode: selectedAreaCode,
              section,
              targetDate,
              scope: isBgTotal
                ? AggregationScope.BG_TOTAL
                : AggregationScope.RETAIL,
              retailerBusinessCd: isBgTotal
                ? undefined
                : selectedRetailerBusinessCd,
              confirmRecordId: recordId,
              bgRetailerBusinessCds: isBgTotal
                ? retailers.map((r) => r.retailerBusinessCd)
                : [],
            }),
          ),
        applyResult: (response) => {
          if (response.result.case === 'bgTotal') {
            const bgTotal = response.result.value
            setConfirmRecordRefs((prev) => [
              ...prev,
              {
                scope: 'BG_TOTAL',
                id: recordId,
                label: response.label,
                bgResults: bgTotal.retailers,
              },
            ])
            // AC-DFD-032 内訳差分ハンドリング: 現在 BG 配下にあるが確定履歴時点に
            // いない retailer は BG 合計から silent に脱落するため、
            // 過少表示による誤読を防ぐ warning toast を出す。preview 追加は
            // 阻止しない (user が事情を把握した上で参照する運用を想定)。
            const missing = bgTotal.missingRetailerBusinessCds
            if (missing.length > 0) {
              appToaster.create({
                type: 'warning',
                title: t(
                  'page:demandForecastDaily.confirmRecordImport.missingRetailers',
                  { count: missing.length, codes: missing.join(', ') },
                ),
              })
            }
          } else if (
            response.result.case === 'retail' &&
            selectedRetailerBusinessCd !== undefined
          ) {
            const retail = response.result.value
            setConfirmRecordRefs((prev) => [
              ...prev,
              {
                scope: 'RETAIL',
                id: recordId,
                label: response.label,
                retailerBusinessCd: selectedRetailerBusinessCd,
                forecastSlots: retail.forecastSlots,
              },
            ])
          }
        },
        onSuccess: () => {
          setIsConfirmRecordDialogOpen(false)
        },
        extraErrorHandlers: [
          {
            // AC-DFD-032 0-件エラー (NO_MATCHING_RETAILERS) / readonly gate
            // (AC-DFC-007, CASE_NOT_EDITABLE) を reason で判別する (旧実装は
            // 両方とも FailedPrecondition を「表示対象の小売がありません」に
            // 一括で丸めていた誤りを是正, Design Note
            // demand-forecast-non-confirm-reason-registry.md Decision #1/#5)。
            matches: (err) => {
              if (!(err instanceof ConnectError)) return false
              resolvedReasonMessage =
                resolveConfirmRecordPreviewDetailedErrorMessage(err, t)
              return resolvedReasonMessage !== undefined
            },
            handle: () => {
              appToaster.create({
                type: 'error',
                title: resolvedReasonMessage,
              })
            },
          },
        ],
        // preview は書込みを伴わないため楽観ロック衝突 (ABORTED) は発生しない契約
        // だが、usePreviewSimpleForecast と同様に防御的に一般エラー扱いにする。
        onAbortedConflict: () => {
          appToaster.create({
            type: 'error',
            title: t(
              'page:demandForecastDaily.confirmRecordImport.importError',
            ),
          })
        },
        onOtherError: () => {
          appToaster.create({
            type: 'error',
            title: t(
              'page:demandForecastDaily.confirmRecordImport.importError',
            ),
          })
        },
      })
    },
    [
      selectedAreaCode,
      targetDate,
      caseId,
      isConfirmRecordRefsAtLimit,
      targetScope,
      selectedRetailerBusinessCd,
      retailers,
      previewConfirmRecordAsync,
      section,
      t,
    ],
  )

  // 参照列 1 件 remove + 対応する confirmRef_<id> hiddenKey の連動除去
  // (codex review #5041、上記 dropAllConfirmRefHiddenKeys と同根拠の単一 key 版)。
  const handleConfirmRecordRefRemove = useCallback(
    (id: string): void => {
      setConfirmRecordRefs((prev) => prev.filter((r) => r.id !== id))
      setLegendHiddenKeys((prev) => {
        const key = confirmRefLegendKey(id)
        if (!prev.has(key)) return prev
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    },
    [setLegendHiddenKeys],
  )

  const handleConfirmRecordRefsClear = useCallback((): void => {
    setConfirmRecordRefs([])
    dropAllConfirmRefHiddenKeys()
  }, [dropAllConfirmRefHiddenKeys])

  const confirmRecordRefColumns = useMemo(
    () =>
      resolveConfirmRecordRefColumns(
        confirmRecordRefs,
        targetScope,
        selectedRetailerBusinessCd,
      ),
    [confirmRecordRefs, targetScope, selectedRetailerBusinessCd],
  )

  // Decision 5: per-retailer データは KomaGrid に渡さず、index の採用コールバックが
  // confirmRecordId から本 flow の内部 state (confirmRecordRefs) を引いて解決する。
  const resolveBgResultsForRef = useCallback(
    (confirmRecordId: string): RetailerPreviewEntry[] | undefined => {
      const ref = confirmRecordRefs.find((r) => r.id === confirmRecordId)
      if (ref === undefined || ref.scope !== 'BG_TOTAL') return undefined
      return ref.bgResults
    },
    [confirmRecordRefs],
  )

  return {
    isConfirmRecordDialogOpen,
    openConfirmRecordDialog,
    closeConfirmRecordDialog,
    selectConfirmRecord,
    confirmRecords,
    isRecordsLoading,
    isRecordsError,
    retryRecords,
    isImporting,
    confirmRecordRefColumns,
    handleConfirmRecordRefRemove,
    handleConfirmRecordRefsClear,
    addedRecordIds,
    isConfirmRecordRefsAtLimit,
    resolveBgResultsForRef,
  }
}
